import { authClient } from '../lib/auth-client'
import { prop } from '@tempots/dom'
import {
  authState,
  AuthUser,
  clearAuthState,
  getPrimaryAgencyId,
  setActiveOrganizationId,
  setLastAuthMessage,
  setPendingVerificationEmail,
  storeSessionUser,
} from '../stores/auth-store'
import { ApiError, setOnUnauthorized } from '../lib/api-client'

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
  fullName: string
  agencyName?: string
}

function normaliseEmail(email: string) {
  return email.trim().toLowerCase()
}

/**
 * Whether the initial session validation has completed.
 * Always starts false in the browser — initAuth() sets it to true once the
 * server session has been checked. This prevents RequireAuth from redirecting
 * before we know whether a valid session cookie exists.
 */
const _inBrowser = typeof window !== 'undefined'
// eslint-disable-next-line tempots/no-module-level-signals -- intentional long-lived global signal for auth readiness state
const authReady = prop(!_inBrowser)

/**
 * Initialize auth state by checking the current session with the server.
 * Call this on app load to hydrate auth state from the cookie session.
 */
async function initAuth(): Promise<AuthUser | null> {
  try {
    const result = await authClient.getSession()

    // Better Auth client returns { data, error }
    if (result.error) {
      // Server returned an error (not "no session") — keep cached localStorage state.
      // The user might still have a valid session; a transient server error
      // shouldn't force them to re-authenticate.
      console.warn('[Auth] getSession returned error:', result.error.message)
      return null
    }

    if (result.data?.user) {
      const user = mapBetterAuthUser(result.data.user)
      storeSessionUser(user)

      // If there's an active org in the session, persist it
      if (result.data.session?.activeOrganizationId) {
        setActiveOrganizationId(result.data.session.activeOrganizationId)
      }

      return user
    }

    // Definitive "no session" — the server confirmed there is no active session.
    clearAuthState()
    return null
  } catch (error) {
    // Network error or unexpected failure — keep cached localStorage state.
    // Don't force sign-out on transient failures.
    console.warn(
      '[Auth] Session check failed:',
      error instanceof Error ? error.message : error
    )
    return null
  } finally {
    authReady.set(true)
  }
}

function mapBetterAuthUser(user: Record<string, unknown>): AuthUser {
  return {
    id: user.id as string,
    email: user.email as string,
    name: (user.name as string) ?? undefined,
    fullName: (user.name as string) ?? undefined,
    role: (user.role as AuthUser['role']) ?? undefined,
    appRole: (user.role as AuthUser['appRole']) ?? undefined,
    image: (user.image as string | null) ?? null,
    // SQLite stores booleans as INTEGER 0/1. Better Auth passes them through
    // without conversion, so we must explicitly coerce to boolean here.
    emailVerified: user.emailVerified === true || user.emailVerified === 1,
    banned: user.banned === true || user.banned === 1,
    createdAt: user.createdAt ? String(user.createdAt) : undefined,
    updatedAt: user.updatedAt ? String(user.updatedAt) : undefined,
  }
}

async function signin(request: LoginRequest) {
  const { data, error } = await authClient.signIn.email({
    email: normaliseEmail(request.email),
    password: request.password,
  })

  if (error) {
    // The server's rate limiter responds 429 with the app error envelope
    // ({ error: { code, message } }), which the Better Auth client doesn't
    // unwrap — error.message is empty here, so map it by status.
    if (error.status === 429) {
      throw new ApiError(
        'Too many login attempts. Please wait a few minutes and try again.',
        429,
        { code: 'RATE_LIMIT_EXCEEDED' }
      )
    }
    // Better Auth returns { message, status } without a code field.
    // Derive a stable code from the message so callers can match on it.
    const code =
      error.status === 403 &&
      error.message?.toLowerCase().includes('email not verified')
        ? 'EMAIL_NOT_VERIFIED'
        : error.code
    throw new ApiError(error.message ?? 'Sign in failed', error.status ?? 401, {
      code,
    })
  }

  if (data?.user) {
    const user = mapBetterAuthUser(data.user)
    storeSessionUser(user)

    // After sign-in, fetch session to get active org, then fall back to first org
    try {
      const { data: session } = await authClient.getSession()
      if (session?.session?.activeOrganizationId) {
        setActiveOrganizationId(session.session.activeOrganizationId)
      } else {
        const { data: orgs } = await authClient.organization.list()
        if (orgs && orgs.length > 0) {
          await authClient.organization.setActive({
            organizationId: orgs[0].id,
          })
          setActiveOrganizationId(orgs[0].id)
        }
      }
    } catch {
      // Ignore — user may not have any orgs yet
    }

    return { user, message: 'Login successful' }
  }

  throw new ApiError('Sign in failed', 401)
}

async function signup(request: RegisterRequest) {
  const { data, error } = await authClient.signUp.email({
    email: normaliseEmail(request.email),
    password: request.password,
    name: request.fullName,
  })

  if (error) {
    throw new ApiError(error.message ?? 'Sign up failed', error.status ?? 400, {
      code: error.code,
    })
  }

  if (data?.user) {
    const user = mapBetterAuthUser(data.user)
    // Store user without considering them signed in (email not verified)
    storeSessionUser(user)
  }

  setPendingVerificationEmail(request.email)
  setLastAuthMessage(
    'Registration successful. Please check your email to verify your account.'
  )

  // If agencyName provided, create organization after sign-up
  if (request.agencyName && data?.user) {
    try {
      const { data: org } = await authClient.organization.create({
        name: request.agencyName,
        slug: request.agencyName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, ''),
      })
      if (org) {
        setActiveOrganizationId(org.id)
      }
    } catch {
      // Organization creation can happen later during onboarding
    }
  }

  return {
    user: data?.user ? mapBetterAuthUser(data.user) : null,
    message:
      'Registration successful. Please check your email to verify your account.',
  }
}

async function resendVerification(email: string): Promise<void> {
  const $fetch = (authClient as unknown as { $fetch: typeof fetch }).$fetch
  await $fetch('/send-verification-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normaliseEmail(email) }),
  })

  setPendingVerificationEmail(normaliseEmail(email))
  setLastAuthMessage(
    'Verification email sent. Please check your inbox (and spam folder).'
  )
}

async function requestPasswordReset(email: string): Promise<string> {
  const { error } = await authClient.requestPasswordReset({
    email: normaliseEmail(email),
    redirectTo: '/reset-password',
  })

  if (error) {
    // Don't throw — always show success to prevent enumeration
    console.warn('Password reset request error:', error.message)
  }

  return 'If an account exists for that email, a reset link has been sent.'
}

async function resetPassword(token: string, password: string): Promise<string> {
  const { error } = await authClient.resetPassword({
    newPassword: password,
    token,
  })

  if (error) {
    // Better Auth answers a bare "Invalid token" for any link that is no
    // longer in the verification table — used, expired, or superseded by
    // a newer invitation/reset email (minting revokes earlier tokens).
    // Tell the user what actually helps. (The legacy bridge sends its own
    // already-friendly copy for expired JWT links; don't rewrite that.)
    const isStaleToken =
      error.code === 'INVALID_TOKEN' ||
      /^invalid token$/i.test(error.message ?? '')
    throw new ApiError(
      isStaleToken
        ? 'This link is no longer valid — it may have expired, already been used, or been replaced by a newer email. Use the link in the most recent email you received, or request a fresh one from the sign-in page.'
        : (error.message ?? 'Password reset failed'),
      error.status ?? 400,
      {
        code: error.code,
      }
    )
  }

  return 'Password reset successfully.'
}

async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<string> {
  const { error } = await authClient.changePassword({
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  })

  if (error) {
    throw new ApiError(
      error.message ?? 'Password change failed',
      error.status ?? 400,
      {
        code: error.code,
      }
    )
  }

  return "Password updated. You've been signed out everywhere else."
}

export interface SessionInfo {
  /** Opaque session token — the handle revokeSession expects. */
  token: string
  createdAt: string
  updatedAt: string
  expiresAt: string
  ipAddress: string | null
  userAgent: string | null
  /** True for the session making this request. */
  isCurrent: boolean
}

/**
 * All active sessions for the signed-in user, current one first.
 * Note: changePassword (revokeOtherSessions) rotates the current
 * session's token — re-fetch after any credential change.
 */
async function listSessions(): Promise<SessionInfo[]> {
  const [{ data: sessions, error }, { data: current }] = await Promise.all([
    authClient.listSessions(),
    authClient.getSession(),
  ])

  if (error) {
    throw new ApiError(
      error.message ?? 'Could not load sessions',
      error.status ?? 400,
      { code: error.code }
    )
  }

  const currentToken = current?.session?.token ?? null
  return (sessions ?? [])
    .map(s => ({
      token: s.token,
      createdAt: String(s.createdAt),
      updatedAt: String(s.updatedAt),
      expiresAt: String(s.expiresAt),
      ipAddress: s.ipAddress ?? null,
      userAgent: s.userAgent ?? null,
      isCurrent: s.token === currentToken,
    }))
    .sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent))
}

/** Sign out one specific session (by its token). */
async function revokeSession(token: string): Promise<void> {
  const { error } = await authClient.revokeSession({ token })
  if (error) {
    throw new ApiError(
      error.message ?? 'Could not sign out that session',
      error.status ?? 400,
      { code: error.code }
    )
  }
}

/** Sign out every session except the current one. */
async function revokeOtherSessions(): Promise<void> {
  const { error } = await authClient.revokeOtherSessions()
  if (error) {
    throw new ApiError(
      error.message ?? 'Could not sign out other sessions',
      error.status ?? 400,
      { code: error.code }
    )
  }
}

/**
 * Permanently delete the account. The server verifies the password and
 * refuses while the user is the sole owner of an agency with members, or
 * while a sole-member agency still has a live subscription.
 */
async function deleteAccount(password: string): Promise<void> {
  const { error } = await authClient.deleteUser({ password })
  if (error) {
    throw new ApiError(
      error.message ?? 'Account deletion failed',
      error.status ?? 400,
      { code: error.code }
    )
  }
  clearAuthState()
}

async function verifyEmail(token: string) {
  // Better Auth handles email verification via GET /verify-email?token=...
  // (paths are relative to the authClient basePath, e.g. /api/auth)
  // After verification with autoSignInAfterVerification, a session cookie is set.
  const $fetch = (authClient as unknown as { $fetch: typeof fetch }).$fetch
  const response = await $fetch(
    `/verify-email?token=${encodeURIComponent(token)}`
  )

  // @better-fetch returns { data, error } — check for errors
  const result = response as unknown as {
    data: { status: boolean; user: unknown } | null
    error: { message?: string; status?: number } | null
  }

  if (result.error) {
    throw new ApiError(
      result.error.message ?? 'Email verification failed',
      result.error.status ?? 400
    )
  }

  // Fetch the session after verification (cookie was set by auto-sign-in)
  const { data: session } = await authClient.getSession()
  if (session?.user) {
    const user = mapBetterAuthUser(session.user)
    storeSessionUser(user)
    setPendingVerificationEmail(null)
    return {
      user,
      message: 'Email verified successfully. You are now signed in.',
    }
  }

  setPendingVerificationEmail(null)
  return { user: null, message: 'Email verified. Please sign in.' }
}

function getPostLoginRedirectPath(): string {
  const primaryAgencyId = getPrimaryAgencyId()
  return primaryAgencyId ? `/${primaryAgencyId}/workspace` : '/onboarding'
}

async function signout() {
  try {
    await authClient.signOut()
  } catch {
    // Best-effort; clear local state regardless
  }
  clearAuthState()
}

// Reactive signals
const isSignedIn = authState.$.user.map(
  user => user != null && user.emailVerified === true
)
const emailVerified = authState.$.emailVerified
const user = authState.$.user

const Auth = {
  initAuth,
  signin,
  signup,
  requestPasswordReset,
  resetPassword,
  changePassword,
  listSessions,
  revokeSession,
  revokeOtherSessions,
  deleteAccount,
  getPostLoginRedirectPath,
  verifyEmail,
  resendVerification,
  emailVerified,
  user,
  signout,
  isSignedIn,
  authReady,
}

// Wire up the API client's 401 handler to clear auth state reactively.
// This lets RequireAuth handle the redirect via the SPA router instead
// of a hard window.location redirect.
setOnUnauthorized(() => clearAuthState())

// Always check the server session on load — a valid cookie may exist even
// when localStorage has no cached user (e.g. fresh tab, cleared storage).
if (_inBrowser) {
  void initAuth()
}

export { ApiError, Auth }
